import { call, put, select, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import { MILE } from 'utils/distanceTypes';
import { GEO_LOCATION } from 'utils/fieldTypes';
import { addWarningToast } from 'modules/toastContainer';
import { ValueType } from './types';
import * as h from './helpers';
import * as actions from './actions';
import * as s from './selectors';
import * as actionTypes from './actionTypes';
import { getDefaultComparator } from './utils/comparators';
import { isTriggerValueType } from './utils';

export const NAME = 'Base Saga';

const getQueryState = ({ queryBuilder: state }) => state;

/*------------------------------------------------------------------------------------------------*/
/* State Action Sagas                                                                             */
/*------------------------------------------------------------------------------------------------*/
export function* updateQuery(searchQueries /* : [] */) {
  try {
    const { combinator, hasDynamicFields } = yield select(getQueryState);
    yield put(actions.updateQuery({ combinator, hasDynamicFields, searchQueries }));
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* updateQueryAndCheckDynamic(
  searchQueries /* : [] */,
  doCheckIfDynamic /* : boolean */,
  forceCheck /* : boolean */,
) {
  try {
    const { combinator, hasDynamicFields, showDynamicFieldToggle } = yield select(getQueryState);
    const defaultCombinator = yield select(h.getDefaultCombinator);
    const isDynamic = h.checkForDynamicFields(
      searchQueries,
      showDynamicFieldToggle,
      hasDynamicFields,
      doCheckIfDynamic,
      forceCheck,
    );

    yield put(
      actions.updateQuery({
        // If there's only one search group, the default combinator should be AND or OR
        // otherwise users will not be filtered down the intended path (see WF-7007)
        combinator: searchQueries.length === 1 ? defaultCombinator : combinator,
        hasDynamicFields: isDynamic,
        searchQueries,
      }),
    );
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* undoQuery() {
  try {
    const { presentState, redoQueue, undoStack } = yield select(getQueryState);
    const previous = undoStack[undoStack.length - 1];
    const newPast = undoStack.slice(0, undoStack.length - 1);
    yield put(
      actions.updateQueryState({
        ...previous,
        undoStack: newPast,
        redoQueue: [presentState, ...redoQueue],
        presentState: previous,
      }),
    );
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* redoQuery() {
  try {
    const { presentState, redoQueue, undoStack } = yield select(getQueryState);
    const next = redoQueue[0];
    const newFuture = redoQueue.slice(1);
    yield put(
      actions.updateQueryState({
        ...next,
        undoStack: [...undoStack, presentState],
        redoQueue: newFuture,
        presentState: next,
      }),
    );
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* initializeQuery({ searchGroup }) {
  try {
    const { searchQueries, showDynamicFieldToggle } = yield select(getQueryState);
    const defaultCombinator = yield select(h.getDefaultCombinator);
    const updatedSearchQueries = h.addSearchGroup(searchQueries, searchGroup);
    const hasDynamicFields = h.checkDynamicFieldsInSearchGroups(
      updatedSearchQueries,
      showDynamicFieldToggle,
    );

    yield put(
      actions.initializeQueryResponse({
        combinator: defaultCombinator,
        hasDynamicFields,
        searchQueries: updatedSearchQueries,
      }),
    );
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* updateAndValidateQuery() {
  try {
    const { searchQueries, showValidations, disallowHandlebarsInQuery } =
      yield select(getQueryState);
    if (showValidations) {
      const validations = h.validateSearchQueries(searchQueries, disallowHandlebarsInQuery);
      const numberOfValidations = validations.length;
      yield put(
        actions.validateQueryResponse(numberOfValidations !== 0, validations, searchQueries),
      );
    }
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* validateQuery({ validationCallback, validationCallbackProps }) {
  try {
    const { searchQueries, disallowHandlebarsInQuery } = yield select(getQueryState);
    const validations = h.validateSearchQueries(searchQueries, disallowHandlebarsInQuery);
    if (validations.length > 0) {
      yield put(actions.validateQueryResponse(true, validations, searchQueries));
      yield addWarningToast('Validation Error: Cannot save. Please check the format of the query.');
    } else {
      yield put(actions.validateQueryResponse(false, [], searchQueries));
      // eslint-disable-next-line prefer-spread
      validationCallback.apply(this, validationCallbackProps);
    }
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

/*------------------------------------------------------------------------------------------------*/
/* Search Group Sagas                                                                             */
/*------------------------------------------------------------------------------------------------*/
export function* addSearchGroup({ searchGroup }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    yield call(updateQuery, h.addSearchGroup(searchQueries, searchGroup));
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* deleteSearchGroup({ searchGroupIndex }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.deleteSearchGroup(searchQueries, searchGroupIndex);
    yield call(updateQueryAndCheckDynamic, updatedGroups, true);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* copySearchGroup({ searchGroupIndex }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    yield call(updateQuery, h.copySearchGroup(searchQueries, searchGroupIndex));
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changeCombinator({ combinator }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    yield put(actions.updateQuery({ combinator, searchQueries }));
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changeSearchGroupCombinator({ combinator, searchGroupIndex }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const searchGroup = h.getSearchGroup(searchQueries, searchGroupIndex);
    const updatedGroup = { ...searchGroup, combinator };
    const updatedGroups = h.updateSearchGroup(searchQueries, searchGroupIndex, updatedGroup);
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

/*------------------------------------------------------------------------------------------------*/
/* Search Query Sagas                                                                             */
/*------------------------------------------------------------------------------------------------*/
export function* addSearchQuery({ searchQuery, searchGroupIndex }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.addSearchQuery(searchQueries, searchGroupIndex, searchQuery);
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* copySearchQuery({ searchGroupIndex, searchQueryIndex }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.copySearchQuery(searchQueries, searchGroupIndex, searchQueryIndex);
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* deleteSearchQuery({ searchQueryIndex, searchGroupIndex }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.deleteSearchQuery(searchQueries, searchGroupIndex, searchQueryIndex);
    yield call(updateQueryAndCheckDynamic, updatedGroups, true);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* updateSearchQuery({ searchQuery, searchQueryIndex, searchGroupIndex }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.updateSearchQuery(
      searchQueries,
      searchGroupIndex,
      searchQueryIndex,
      searchQuery,
    );
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* moveSearchQuery({
  searchGroupId,
  searchQueryIndex,
  newSearchGroupId,
  newSearchQueryIndex,
}) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.moveSearchQuery(
      searchQueries,
      h.getSearchGroupIndexFromId(searchGroupId),
      searchQueryIndex,
      h.getSearchGroupIndexFromId(newSearchGroupId),
      newSearchQueryIndex,
    );
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* reorderSearchQuery({ searchQueryIndex, newSearchQueryIndex, searchGroupId }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.reorderSearchQuery(
      searchQueries,
      h.getSearchGroupIndexFromId(searchGroupId),
      searchQueryIndex,
      newSearchQueryIndex,
    );
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changeProperty({ property, searchGroupIndex, searchQueryIndex }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const searchQuery = { dataType: property };

    const updatedGroups = h.updateSearchQuery(
      searchQueries,
      searchGroupIndex,
      searchQueryIndex,
      searchQuery,
    );
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changePropertyRefinement({
  minMatch,
  maxMatch,
  searchGroupIndex,
  searchQueryIndex,
}) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const searchQuery = { minMatch, maxMatch };

    const updatedGroups = h.updateSearchQuery(
      searchQueries,
      searchGroupIndex,
      searchQueryIndex,
      searchQuery,
    );
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

/*------------------------------------------------------------------------------------------------*/
/* Criteria Properties Sagas                                                                      */
/*------------------------------------------------------------------------------------------------*/
export const updateCriteriaProperty = (
  criteriaProperty /*: {} */,
  searchGroups /*: [] */,
  indexes /*: {} */,
) => {
  const { searchGroupIndex, searchQueryIndex, criteriaPropertyIndex } = indexes;
  return h.updateCriteriaProperty(
    searchGroups,
    searchGroupIndex,
    searchQueryIndex,
    criteriaPropertyIndex,
    criteriaProperty,
  );
};

export function* addCriteriaProperty({ criteriaProperty, searchGroupIndex, searchQueryIndex }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.addCriteriaProperty(
      searchQueries,
      searchGroupIndex,
      searchQueryIndex,
      criteriaProperty,
    );
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* copyCriteriaProperty({
  criteriaPropertyIndex,
  searchGroupIndex,
  searchQueryIndex,
}) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.copyCriteriaProperty(
      searchQueries,
      searchGroupIndex,
      searchQueryIndex,
      criteriaPropertyIndex,
    );
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* deleteCriteriaProperty({
  criteriaPropertyIndex,
  searchGroupIndex,
  searchQueryIndex,
}) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const updatedGroups = h.deleteCriteriaProperty(
      searchQueries,
      searchGroupIndex,
      searchQueryIndex,
      criteriaPropertyIndex,
    );
    yield call(updateQueryAndCheckDynamic, updatedGroups, true);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changeCastTypeSaga({ castType, ...indexes }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const criteriaProperty = { castType };
    const updatedGroups = updateCriteriaProperty(criteriaProperty, searchQueries, indexes);
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changeField({ field, fieldType, valueType, ...indexes }) {
  try {
    const { emptyCriteriaProperty, searchQueries } = yield select(getQueryState);
    const isGeo = fieldType === GEO_LOCATION;
    const comparatorType = getDefaultComparator(fieldType);
    const geoProperties = isGeo
      ? { distance: { distance: 1, unit: MILE }, location: { lat: 0, lon: 0 } }
      : { distance: undefined, location: undefined };

    // need to fetch the existing criteria property field
    // that we're overriding, and then apply its "dataType" to the newer version
    // its bizarre, i know, but there's a lot of moving pieces here
    // that i feel unequipped to make changes to without knowing
    // how it will affect everything else.
    const existingCriteriaProperty = yield select((state) =>
      s.selectCriteriaProperty(state, { ...indexes }),
    );

    const criteriaProperty = {
      ...emptyCriteriaProperty,
      dataType: existingCriteriaProperty.dataType,
      field,
      fieldType,
      comparatorType,
      dynamicValue: h.getDynamicValue(valueType),
      ...geoProperties,
    };
    const updatedGroups = updateCriteriaProperty(criteriaProperty, searchQueries, indexes);
    yield call(updateQueryAndCheckDynamic, updatedGroups, true, true);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changeComparator({ comparator, valueType, ...indexes }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const criteriaProperty = h.getUpdatedCriteriaPropertyForComparator(comparator, valueType);
    const updatedGroups = updateCriteriaProperty(criteriaProperty, searchQueries, indexes);
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changeDynamicValue({ dynamicValue, ...indexes }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const criteriaProperty = { dynamicValue };
    const updatedGroups = updateCriteriaProperty(criteriaProperty, searchQueries, indexes);
    yield call(updateQueryAndCheckDynamic, updatedGroups, true, true);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changeGeoValue({ lat, lon, distance, unit, ...indexes }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const criteriaProperty = { distance: { distance, unit }, location: { lat, lon }, value: '' };
    const updatedGroups = updateCriteriaProperty(criteriaProperty, searchQueries, indexes);
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* changeValue({ value, propertyName = 'value', ...indexes }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    // This allows for the setting of different properties
    // EG: setting `values`, which is required by multiselects
    const criteriaProperty = { [propertyName]: value };
    const updatedGroups = updateCriteriaProperty(criteriaProperty, searchQueries, indexes);
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* toggleDynamic({ valueType, ...indexes }) {
  try {
    const { searchQueries } = yield select(getQueryState);
    const dynamicValue = h.getDynamicValue(valueType);

    // We can't actually save value types, so we store trigger values in the value field.
    const value = isTriggerValueType(valueType) ? ValueType.Trigger : '';

    const criteriaProperty = { valueType, dynamicValue, value };
    const updatedGroups = updateCriteriaProperty(criteriaProperty, searchQueries, indexes);
    yield call(updateQueryAndCheckDynamic, updatedGroups, true, true);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

export function* clearValue(indexes) {
  try {
    const { searchQueries } = yield select(getQueryState);
    // Set all except dataType: '', field: '', and fieldType: '' to empty
    const criteriaProperty = {
      comparatorType: '',
      value: '',
      castType: undefined,
      location: undefined,
      dynamicValue: undefined,
      distance: undefined,
    };
    const updatedGroups = updateCriteriaProperty(criteriaProperty, searchQueries, indexes);
    yield call(updateQuery, updatedGroups);
  } catch (error) {
    yield put(actions.updateQueryError(error));
  }
}

/*------------------------------------------------------------------------------------------------*/
/* Fields Sagas                                                                                   */
/*------------------------------------------------------------------------------------------------*/
export function* updateFieldsForProperty({ fields, property }) {
  try {
    const { fields: queryFields } = yield select(getQueryState);
    const updatedFields = { ...queryFields, [property]: fields };
    yield put(actions.updateFieldsForPropertyResponse(updatedFields));
  } catch (error) {
    yield put(actions.updateFieldsForPropertyError(error));
  }
}

/*------------------------------------------------------------------------------------------------*/
/* Field Options Sagas                                                                            */
/*------------------------------------------------------------------------------------------------*/
export function* updateOptionsForField({ field, property, options }) {
  try {
    const { fieldOptions } = yield select(getQueryState);
    const currentPropertyOptions = { ...fieldOptions[property], [field]: options };
    const updatedOptions = { ...fieldOptions, [property]: currentPropertyOptions };
    yield put(actions.updateOptionsForFieldResponse(updatedOptions));
  } catch (error) {
    yield put(actions.updateOptionsForFieldError(error));
  }
}

addToRootSaga([
  takeLatest(actionTypes.ADD_CRITERIA_PROPERTY, addCriteriaProperty),
  takeLatest(actionTypes.ADD_SEARCH_GROUP, addSearchGroup),
  takeLatest(actionTypes.ADD_SEARCH_QUERY, addSearchQuery),
  takeLatest(actionTypes.CHANGE_CAST_TYPE, changeCastTypeSaga),
  takeLatest(actionTypes.CHANGE_COMBINATOR, changeCombinator),
  takeLatest(actionTypes.CHANGE_COMPARATOR, changeComparator),
  takeLatest(actionTypes.CHANGE_DYNAMIC_VALUE, changeDynamicValue),
  takeLatest(actionTypes.CHANGE_FIELD, changeField),
  takeLatest(actionTypes.CHANGE_GEO_VALUE, changeGeoValue),
  takeLatest(actionTypes.CHANGE_PROPERTY, changeProperty),
  takeLatest(actionTypes.CHANGE_PROPERTY_REFINEMENT, changePropertyRefinement),
  takeLatest(actionTypes.CHANGE_SEARCH_GROUP_COMBINATOR, changeSearchGroupCombinator),
  takeLatest(actionTypes.CHANGE_VALUE, changeValue),
  takeLatest(actionTypes.CLEAR_VALUE, clearValue),
  takeLatest(actionTypes.COPY_CRITERIA_PROPERTY, copyCriteriaProperty),
  takeLatest(actionTypes.COPY_SEARCH_GROUP, copySearchGroup),
  takeLatest(actionTypes.COPY_SEARCH_QUERY, copySearchQuery),
  takeLatest(actionTypes.DELETE_CRITERIA_PROPERTY, deleteCriteriaProperty),
  takeLatest(actionTypes.DELETE_SEARCH_GROUP, deleteSearchGroup),
  takeLatest(actionTypes.DELETE_SEARCH_QUERY, deleteSearchQuery),
  takeLatest(actionTypes.INITIALIZE_QUERY, initializeQuery),
  takeLatest(actionTypes.MOVE_SEARCH_QUERY, moveSearchQuery),
  takeLatest(actionTypes.TOGGLE_DYNAMIC, toggleDynamic),
  takeLatest(actionTypes.REDO_QUERY, redoQuery),
  takeLatest(actionTypes.REORDER_SEARCH_QUERY, reorderSearchQuery),
  takeLatest(actionTypes.UNDO_QUERY, undoQuery),
  takeLatest(actionTypes.UPDATE_FIELDS_FOR_PROPERTY, updateFieldsForProperty),
  takeLatest(actionTypes.UPDATE_OPTIONS_FOR_FIELD, updateOptionsForField),
  takeLatest(actionTypes.UPDATE_SEARCH_QUERY, updateSearchQuery),
  takeLatest(actionTypes.UPDATE_QUERY, updateAndValidateQuery),
  takeLatest(actionTypes.VALIDATE_QUERY, validateQuery),
]);
